<template>
  <div data-component-group="uri-form" data-component="add-new">
    <slot name="beforeForm" />

    <form @:submit.prevent="onSubmit">
      <magic-container>
        <v-text-field
          v-if="hasItem && shopName !== ''"
          v-model="shopName"
          prepend-inner-icon="store"
          :label="$t('merchant_shop')"
          required
          type="text"
          :readonly="isDisabled"
          :autofocus="true"
        />

        <v-text-field
          v-model="uriName"
          prepend-inner-icon="link"
          :label="label"
          required
          type="text"
          :readonly="isDisabled"
          :error-messages="uriNameErrors"
          @blur="v$.uriName.$touch()"
        />
      </magic-container>

      <magic-container>
        <v-text-field
          v-model="sellerId"
          prepend-inner-icon="feed"
          :label="$t('seller_id_placeholder')"
          type="text"
          required
          :readonly="isDisabled"
        />

        <v-text-field
          v-if="canViewKeywords"
          v-model="keywords"
          prepend-inner-icon="label_outline"
          :label="$t('keywords')"
          required
          type="text"
          :readonly="isDisabled"
        />
      </magic-container>

      <div class="form-actions">
        <v-btn
          v-if="onCancel"
          variant="text"
          class="trigger cancel-uri"
          @click="cancel"
        >
          {{ cancelTriggerLabel }}
        </v-btn>

        <v-btn
          v-if="onSubmit && !isDisabled"
          color="primary"
          class="trigger"
          variant="flat"
          :loading="isLoading"
          @click="submit"
        >
          {{ confirmTriggerLabel }}
        </v-btn>
      </div>
    </form>
  </div>
</template>

<script>
  // todo: move the form to a contract form
  import { useVuelidate } from '@vuelidate/core'
  import { required, url } from '@vuelidate/validators';
  import { isStr, isObj, isArr, isNum } from '../../../utils';

  export default {
    setup () {
    return {
      v$: useVuelidate()
    }
  },
    validations() {
      return {
        uriName: {
          required,
          url,
        },
      };
    },
    props: {
      uri: {
        type: Object,
        default: undefined,
      },
      cancelTriggerLabel: {
        type: String,
        default: 'Cancel',
      },
      confirmTriggerLabel: {
        type: String,
        default: 'Create URI',
      },
      label: {
        type: String,
        default: 'URI',
      },
      isLoading: {
        type: Boolean,
        default: false,
      },
      isDisabled: {
        type: Boolean,
        default: false,
      },
      isSingleLine: {
        type: Boolean,
        default: false,
      },
      isUrl: {
        type: Boolean,
        default: false,
      },
      onCancel: {
        type: Function,
        default: undefined,
      },
      onSubmit: {
        type: Function,
        default: undefined,
      },
    },
    data: () => ({
      shopName: '',
      uriName: '',
      keywords: '',
      sellerId: '',
      isSubmitted: false,
    }),
    computed: {
      hasItem() {
        return isObj(this.uri);
      },
      uriNameErrors() {
        return this.v$.uriName.$errors.map(error => {
          if (error.$validator === 'required') {
            return `${this.label} ${this.$t('is_required')}`;
          }
          if (error.$validator === 'url') {
            return `${this.$t('must_be_a_valid')} ${this.label}`;
          }
          return error.$message;
        });
      },
      canViewKeywords() {
        return (
          (this.shopName === '' && this.keywords === '') || this.keywords !== ''
        );
      },
    },
    watch: {
      uri(nextUri) {
        this.clear();
        this.onReceiveUri(nextUri);
      },
    },
    created() {
      this.onReceiveUri(this.uri);
    },
    methods: {
      onReceiveUri(nextUri) {
        if (isObj(nextUri) && isNum(nextUri.id)) {
          this.extractUriData(nextUri);
        }
      },
      cancel() {
        this.clear();
        this.onCancel();
      },
      submit() {
        this.v$.$touch();

        if (!this.canSubmitRequest() || this.v$.$error || this.v$.pending) {
          return false;
        }

        this.addUri();
      },
      clear() {
        this.v$.$reset();
        this.shopName = '';
        this.uriName = '';
        this.keywords = '';
        this.sellerId = '';
        this.isSubmitted = false;
      },
      addUri() {
        const { uriName, keywords, sellerId } = this;

        const uri = {
          uri: uriName,
          keywords,
          seller_id: sellerId,
        };

        return this.onSubmit(uri).then((res) => {
          if (!this.hasItem) {
            return this.clear();
          }

          return res;
        });
      },
      canSubmitRequest() {
        const { uriName, isSubmitted } = this;

        return uriName !== '' && !isSubmitted;
      },
      extractUriData(item) {
        const { uri, keywords, seller_id, shop } = item;

        this.shopName = isObj(shop) ? shop.name : '';
        this.uriName = uri;
        this.sellerId = seller_id || '';
        this.keywords = isArr(keywords)
          ? keywords.join(', ')
          : isStr(keywords)
            ? keywords
            : '';
      },
    },
  };
</script>

<style lang="scss" scoped>
@use 'sass:map';
@use '../../../assets/styles/theme' as *;

[data-component-group='uri-form'][data-component='add-new'] {
  padding: $spacing-xl;
  background-color: rgb(var(--v-theme-surface));

  .padding-right {
    padding-right: 20px;
  }

// TODO replace with form-buttons
  .form-actions {
    display: flex;
    justify-content: flex-end;
    align-items: center;

    .v-btn {
      margin: 0 $spacing-sm 0 0;

      &:last-child {
        margin-right: 0;
      }
    }
  }
}
</style>
